import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Flame } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import PageTransition from '../components/layout/PageTransition';
import ScrollReveal from '../components/layout/ScrollReveal';
import { getPost } from '../lib/api';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getPost(slug)
      .then((res) => {
        setPost(res.data.post);
        document.title = res.data.post.title;
      })
      .catch(() => setError('Post not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <PageTransition>
        <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <Skeleton className="h-4 w-24 mb-8 bg-white/10" />
          <Skeleton className="h-10 w-3/4 mb-4 bg-white/10" />
          <Skeleton className="h-48 w-full mb-6 rounded-lg bg-white/10" />
          <Skeleton className="h-4 w-full mb-2 bg-white/10" />
          <Skeleton className="h-4 w-full mb-2 bg-white/10" />
          <Skeleton className="h-4 w-2/3 bg-white/10" />
        </div>
      </PageTransition>
    );
  }

  if (error || !post) {
    return (
      <PageTransition>
        <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 text-center">
          <h1 className="text-3xl font-semibold mb-4">Post Not Found</h1>
          <p className="text-white/55 mb-6">The journal entry you're looking for doesn't exist.</p>
          <Link to="/journal">
            <Button variant="outline" className="bg-white/5 border-white/10">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Journal
            </Button>
          </Link>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <section className="relative pt-24 sm:pt-28 lg:pt-36 pb-14 sm:pb-20">
        <div className="absolute inset-0 bg-fire-engine" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal hero>
            <Link to="/journal" className="inline-flex items-center gap-1 text-xs text-white/55 hover:text-white/80 mb-6 transition-colors">
              <ArrowLeft className="h-3 w-3" />
              Back to Journal
            </Link>

            <div className="flex flex-wrap gap-2 mb-4">
              {(post.tags || []).map((tag) => (
                <Badge key={tag} className="bg-[rgba(232,114,42,0.10)] text-[rgba(255,180,80,0.85)] border-[rgba(232,114,42,0.18)] text-[10px]">{tag}</Badge>
              ))}
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.04em] leading-[0.95] mb-4" data-testid="blog-post-title">
              {post.title}
            </h1>

            <div className="flex items-center gap-4 text-xs text-white/50">
              <div className="flex items-center gap-1">
                <Flame className="h-3 w-3 text-[#E8722A]" />
                <span>{post.author || 'Visualte Team'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{new Date(post.displayDate || post.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 relative">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal>
            {post.coverImageUrl && (
              <div className="rounded-[6px] overflow-hidden mb-8 border border-white/10">
                <img src={post.coverImageUrl} alt={post.title} className="w-full object-cover" />
              </div>
            )}

            <div className="prose prose-invert prose-sm max-w-none"
              style={{ lineHeight: 1.8, color: 'rgba(255,255,255,0.72)' }}
              data-testid="blog-post-content"
            >
              {post.content.split('\n').map((para, i) => (
                para.trim() ? <p key={i} className="mb-4 text-sm md:text-base leading-relaxed">{para}</p> : null
              ))}
            </div>

            <Separator className="my-10 bg-white/8" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Flame className="h-4 w-4 text-[#E8722A]" />
                <span className="text-xs text-white/40">Visualte Journal</span>
              </div>
              <Link to="/journal" className="text-xs text-[#E8722A] hover:underline">
                More entries
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PageTransition>
  );
}
